"use client"

// components/tracker/drugs-page.tsx
//
// The Drugs overview (FE-DRUG-PAGE-1 Phase 4a-ii, Paper PZH-0 round 1): the
// member's tracked drugs as cards on the carved Drugs sheet, with a
// client-side search over `searchTerms`, four facet filters over `facets`,
// a sort, a Cards / Compact list toggle remembered per browser, and the
// quick view drawer (Q61-0) opened from a card. House copy: no em dashes.
//
// THE WIRING IS THE COMPANIES PAGE'S: a static server page renders the
// shell with the four load-bearing props; this component reads the watch
// from `useTrackerShell()` and fetches `/api/v2/landscapes/[id]/drugs`. The
// search and filters are client-side here because the payload IS the whole
// watch (a watch holds a handful of drugs, never a paged list), unlike the
// Companies lens which pages a ranked lane array and filters on the
// request. A failed read branches on the envelope's `code`, never `message`.
//
// `DrugIdentityProvider` is mounted from `drugs[].{name, assetId}` so every
// sphere below (cards, rows, the drawer) resolves through `useDrugAvatar`
// with `data-identity`, and a card and its drawer paint the same URI.

import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react"
import Link from "next/link"
import { usePathname } from "next/navigation"

import { DrugCard, DrugRow } from "@/components/tracker/drug-card"
import {
  DrugIdentityProvider,
  type AssetIdByDrug,
} from "@/components/tracker/drug-identity-context"
import { DrugQuickView } from "@/components/tracker/drug-quick-view"
import { DrugsSheet } from "@/components/tracker/drugs-sheet"
import { FilterDropdown, type FilterOption } from "@/components/tracker/filter-dropdown"
import { ExpiredUpgradeCard } from "@/components/tracker/upgrade-rail"
import { useTrackerShell } from "@/components/tracker/tracker-shell-context"
import { hasWatch } from "@/components/tracker/use-tracker-watchlist"
import { useIsMobile } from "@/hooks/use-mobile"
import type {
  DrugCard as DrugCardData,
  DrugsOverview,
} from "@/lib/drugs/load-drugs-overview"

import styles from "@/components/tracker/drugs-page.module.css"

type View = "cards" | "list"
type SortKey = "custom" | "name" | "trials"
type FacetKey = "therapyAreas" | "conditions" | "approvedIndications" | "targetClass"

/** Namespaced, so it cannot collide with another tracker key. */
const VIEW_STORAGE_KEY = "complyrx.tracker.drugs.view"

const SORT_OPTIONS: FilterOption[] = [
  { value: "custom", label: "Custom order" },
  { value: "name", label: "Name" },
  { value: "trials", label: "Trials" },
]

const SORT_LABELS: Record<SortKey, string> = {
  custom: "Custom order",
  name: "Name",
  trials: "Trials",
}

const FACETS: ReadonlyArray<{ key: FacetKey; label: string }> = [
  { key: "therapyAreas", label: "Therapy area" },
  { key: "conditions", label: "Studied condition" },
  { key: "approvedIndications", label: "Approved indication" },
  { key: "targetClass", label: "Target / class" },
]

type LoadState =
  | { status: "loading" }
  | { status: "ready"; overview: DrugsOverview }
  /** The route withheld the overview (`gated: true`): the member's free week ended. */
  | { status: "gated" }
  | { status: "error"; code: string }

function readStoredView(): View {
  try {
    const v = window.localStorage.getItem(VIEW_STORAGE_KEY)
    return v === "list" ? "list" : "cards"
  } catch {
    return "cards"
  }
}

function writeStoredView(view: View) {
  try {
    window.localStorage.setItem(VIEW_STORAGE_KEY, view)
  } catch {
    // Private mode or a blocked store: the choice lives for the session.
  }
}

/** The values one drug carries for a facet; empty when that feed failed. */
function facetValues(card: DrugCardData, key: FacetKey): string[] {
  if (key === "targetClass") {
    const out: string[] = []
    if (card.classLine) out.push(card.classLine)
    if (card.target) out.push(card.target.name)
    return out
  }
  return card.facets?.[key] ?? []
}

/** Distinct values across the watch with the count of drugs carrying each. */
function facetOptions(drugs: DrugCardData[], key: FacetKey): FilterOption[] {
  const counts = new Map<string, number>()
  for (const d of drugs) {
    for (const v of new Set(facetValues(d, key))) {
      counts.set(v, (counts.get(v) ?? 0) + 1)
    }
  }
  return [...counts]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0], "en-GB"))
    .map(([value, count]) => ({ value, label: value, count }))
}

/** Case-insensitive substring over the search terms, or the name itself
 * when the terms did not load (a fallback card has none). */
function matchesQuery(card: DrugCardData, q: string): boolean {
  if (!q) return true
  const needle = q.toLowerCase()
  if (card.name.toLowerCase().includes(needle)) return true
  if (card.canonicalName?.toLowerCase().includes(needle)) return true
  return card.searchTerms?.some((t) => t.toLowerCase().includes(needle)) ?? false
}

function errorCopy(code: string): string {
  switch (code) {
    case "not_found":
      return "This watch could not be found. Pick another watch from the rail."
    case "rate_limited":
      return "Too many requests. Wait a minute and reload the page."
    case "unauthorized":
    case "forbidden":
      return "Sign in again to see your tracked drugs."
    default:
      return "Your drugs could not be loaded. Reload the page to try again."
  }
}

/**
 * Phase SCALE (2026-09-28): the Drugs page's own canvas, NOT DigestCanvas.
 *
 * DigestCanvas zooms a fixed sheet 0.9x to 1.3x, and at 0.9x this page's
 * 12px floor rendered at 10.8px; the founder ruled out raising the shared
 * floor and ruled out inflating the type (2026-09-28). This wrapper keeps
 * what DigestCanvas gives the page apart from the zoom: the #F8F9FA ground
 * the carve's notches are painted in, the same padding, and the mobile
 * stack with a footer slot after the sheet. The sheet itself is fluid to
 * 1147 (drugs-sheet.module.css), so nothing here measures anything.
 * `digest-canvas.tsx` is untouched; an opt-in `natural` prop on it is a
 * separate scope decision (plan, Phase SCALE).
 */
function DrugsCanvas({
  children,
  footer = null,
}: {
  children: React.ReactNode
  /** Mobile only, as DigestCanvas: the desktop sheet ends on its own carve. */
  footer?: React.ReactNode
}) {
  const isMobile = useIsMobile()
  return (
    <div className={`${styles.canvas} ${isMobile ? styles.canvasMobile : ""}`}>
      <div data-stack={isMobile ? "true" : "false"} className={styles.canvasStack}>
        {children}
      </div>
      {isMobile ? footer : null}
    </div>
  )
}

export function DrugsPage() {
  const { watchlist, surface } = useTrackerShell()
  const pathname = usePathname()

  const notice =
    "rounded-[18px] border border-ink/10 bg-[#f8f9fa] px-8 py-10 text-center " +
    "shadow-[4px_4px_10px_rgba(13,31,45,0.12),-3px_-3px_8px_rgba(255,255,255,0.95)]"

  const landscapeId =
    watchlist.status === "ready" ? watchlist.landscapeId : null

  const [state, setState] = useState<LoadState>({ status: "loading" })
  const [query, setQuery] = useState("")
  const [selected, setSelected] = useState<Record<FacetKey, string[]>>({
    therapyAreas: [],
    conditions: [],
    approvedIndications: [],
    targetClass: [],
  })
  const [sort, setSort] = useState<SortKey>("custom")
  // The stored choice is read after mount: the server render has no
  // localStorage, and a hydration mismatch is worse than one frame of cards.
  const [view, setView] = useState<View>("cards")
  useEffect(() => {
    setView(readStoredView())
  }, [])
  const chooseView = useCallback((v: View) => {
    setView(v)
    writeStoredView(v)
  }, [])

  /** The card whose quick view is open; null when closed. */
  const [openCard, setOpenCard] = useState<DrugCardData | null>(null)
  /** The control that opened it, from the click's `currentTarget`; focus goes back there on close. */
  const openerRef = useRef<HTMLElement | null>(null)
  /* A11Y 2026-09-28: the search input's id for its visible label, the
     input ref for the clear button's focus return, and the id of the
     visible note that explains the inert explore button. */
  const searchId = useId()
  const searchRef = useRef<HTMLInputElement | null>(null)
  const exploreNoteId = useId()
  const openQuickView = useCallback((card: DrugCardData, opener: HTMLElement | null) => {
    openerRef.current = opener
    setOpenCard(card)
  }, [])
  const closeQuickView = useCallback(() => setOpenCard(null), [])

  // One read per watch. The abort covers the request; the generation check
  // stops a response that was already parsed when the watch changed.
  useEffect(() => {
    if (!landscapeId) return
    const ac = new AbortController()
    let live = true
    setState({ status: "loading" })
    setOpenCard(null)
    const run = async () => {
      try {
        const res = await fetch(`/api/v2/landscapes/${landscapeId}/drugs`, {
          headers: { Accept: "application/json" },
          signal: ac.signal,
        })
        if (!res.ok) {
          let code = `http_${res.status}`
          try {
            const body = (await res.json()) as { error?: { code?: string } }
            if (body?.error?.code) code = body.error.code
          } catch {
            // No envelope: keep the status code.
          }
          if (live) setState({ status: "error", code })
          return
        }
        const json = (await res.json()) as DrugsOverview & { gated?: boolean }
        // The W3 trial gate (Phase GATE): the route answers 200 with
        // `gated: true` and an empty overview, the Changes route's posture.
        if (json.gated === true) {
          if (live) setState({ status: "gated" })
          return
        }
        if (live) setState({ status: "ready", overview: json })
      } catch (err) {
        if (err instanceof DOMException && err.name === "AbortError") return
        if (live) setState({ status: "error", code: "network" })
      }
    }
    void run()
    return () => {
      live = false
      ac.abort()
    }
  }, [landscapeId])

  const overview = state.status === "ready" ? state.overview : null
  const drugs = useMemo(() => overview?.drugs ?? [], [overview])

  const assetIdByDrug = useMemo<AssetIdByDrug>(() => {
    const out: AssetIdByDrug = {}
    for (const d of drugs) out[d.name] = d.assetId
    return out
  }, [drugs])

  const options = useMemo(
    () =>
      Object.fromEntries(
        FACETS.map((f) => [f.key, facetOptions(drugs, f.key)]),
      ) as Record<FacetKey, FilterOption[]>,
    [drugs],
  )

  const toggleFacet = useCallback((key: FacetKey, value: string) => {
    setSelected((prev) => {
      const cur = prev[key]
      const next = cur.includes(value)
        ? cur.filter((v) => v !== value)
        : [...cur, value]
      return { ...prev, [key]: next }
    })
  }, [])

  // OR within a facet, AND across facets, then the search, then the sort.
  const visible = useMemo(() => {
    const q = query.trim()
    const filtered = drugs.filter((d) => {
      if (!matchesQuery(d, q)) return false
      for (const f of FACETS) {
        const want = selected[f.key]
        if (want.length === 0) continue
        const have = facetValues(d, f.key)
        if (!want.some((w) => have.includes(w))) return false
      }
      return true
    })
    if (sort === "name") {
      return [...filtered].sort((a, b) =>
        a.name.localeCompare(b.name, "en-GB", { sensitivity: "base" }),
      )
    }
    if (sort === "trials") {
      // Most trials first; a null count (no record, or a failed read) last.
      return [...filtered].sort(
        (a, b) =>
          (b.trialCount ?? -1) - (a.trialCount ?? -1) ||
          a.name.localeCompare(b.name, "en-GB", { sensitivity: "base" }),
      )
    }
    return filtered
  }, [drugs, query, selected, sort])

  const anyFilter =
    query.trim().length > 0 || FACETS.some((f) => selected[f.key].length > 0)

  // A resolved card with null `facets` is one whose facet feeds FAILED (a
  // fallback card has no record to read and says so on its own face). Such
  // a card cannot match any facet value, so the filters cannot see it; the
  // page says so rather than letting it vanish silently (review fix 6).
  const resolvedCount = useMemo(() => drugs.filter((d) => d.assetId !== null).length, [drugs])
  const noFacetCount = useMemo(
    () => drugs.filter((d) => d.assetId !== null && d.facets === null).length,
    [drugs],
  )
  const facetsUnavailable = resolvedCount > 0 && noFacetCount === resolvedCount

  if (watchlist.status === "signed_out") {
    return (
      <div className={notice}>
        <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-ink/45">
          Trial Tracker
        </p>
        <h1 className="mt-2 text-xl font-semibold tracking-tight text-ink">
          Drugs is a member page
        </h1>
        <p className="mx-auto mt-2 max-w-md text-sm text-ink/60">
          Sign in to your tracker to see it.
        </p>
        <Link
          href={`/login?next=${encodeURIComponent(pathname ?? "/tools/trial-tracker/drugs")}`}
          className="mt-6 inline-flex h-10 items-center rounded-full bg-ink px-5 text-sm font-medium text-white transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink/40"
        >
          Sign in
        </Link>
      </div>
    )
  }

  // The watchlist itself failed to load: without it there is no landscape
  // id to fetch, and the skeleton would sit there for ever (review fix 5).
  if (watchlist.status === "error") {
    return (
      <div className={notice} role="alert">
        <h1 className="text-xl font-semibold tracking-tight text-ink">
          Your watch could not be loaded
        </h1>
        <p className="mx-auto mt-2 max-w-md text-sm text-ink/60">
          The tracker could not read your watchlist. Reload the page to try again.
        </p>
      </div>
    )
  }

  if (watchlist.status === "ready" && !hasWatch(watchlist, surface)) {
    return (
      <div className={notice}>
        <h1 className="text-xl font-semibold tracking-tight text-ink">
          Track a drug to see it here
        </h1>
        <p className="mx-auto mt-2 max-w-md text-sm text-ink/60">
          Every drug you track lands here as a card, connected to its trials,
          indications, companies and latest activity.
        </p>
        <Link
          href="/tools/trial-tracker"
          className="mt-6 inline-flex h-10 items-center rounded-full bg-ink px-5 text-sm font-medium text-white transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink/40"
        >
          Track a drug
        </Link>
      </div>
    )
  }

  // Two sources say "expired": the shell's surface (`/landscapes/[id]`) and
  // this page's own read (`/landscapes/[id]/drugs`, Phase GATE). Either one
  // renders the tease card the Changes page shows; the counts come from the
  // surface when it has them.
  if (surface?.gated || state.status === "gated") {
    return (
      <ExpiredUpgradeCard
        line="Every drug you track, connected to its trials, indications and companies. Members only now."
        counts={surface?.gatedCounts}
      />
    )
  }

  const total = overview?.landscape.drugCount ?? null
  const countLine =
    overview && total !== null
      ? `${total.toLocaleString("en-GB")} tracked ${total === 1 ? "drug" : "drugs"} · ${overview.landscape.name}`
      : null

  // No note under the empty state: "Showing all 0 drugs" says nothing.
  const scopeLeft = overview && drugs.length > 0
    ? anyFilter
      ? `Showing ${visible.length.toLocaleString("en-GB")} of ${drugs.length.toLocaleString("en-GB")} ${drugs.length === 1 ? "drug" : "drugs"}`
      : `Showing all ${drugs.length.toLocaleString("en-GB")} ${drugs.length === 1 ? "drug" : "drugs"}`
    : null
  const noFacetNote =
    noFacetCount > 0
      ? `${noFacetCount.toLocaleString("en-GB")} ${noFacetCount === 1 ? "drug has" : "drugs have"} no filter data`
      : null

  return (
    <DrugIdentityProvider assetIdByDrug={assetIdByDrug}>
      <div className={styles.page}>
        {/* THE SHEET IS THE PAGE. Phase SCALE: the local DrugsCanvas, not
            DigestCanvas, so the sheet renders at zoom 1 and fits the width
            it is given (see DrugsCanvas above). */}
        <DrugsCanvas>
          <DrugsSheet
            countLine={countLine}
            body={
              <>
                {/* Q28-0: the search field and the explore button. The
                    button is DRAWN AND INERT: the explore search surface
                    (Phase 3b's route) has no UI in this phase. */}
                <div className={styles.searchRow}>
                  <div className={styles.searchField}>
                    {/* A11Y: a visible label, not placeholder-only. */}
                    <label htmlFor={searchId} className={styles.searchLabel}>
                      Search
                    </label>
                    <svg
                      className={styles.searchIcon}
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden
                    >
                      <circle cx="11" cy="11" r="7" />
                      <path d="m20 20-3.5-3.5" />
                    </svg>
                    <input
                      id={searchId}
                      ref={searchRef}
                      type="search"
                      className={styles.searchInput}
                      placeholder="A drug, brand, code or identifier"
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                    />
                    {/* A11Y: a named clear control while the field has
                        text; focus returns to the input. */}
                    {query ? (
                      <button
                        type="button"
                        className={styles.searchClear}
                        aria-label="Clear search"
                        onClick={() => {
                          setQuery("")
                          searchRef.current?.focus()
                        }}
                      >
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          aria-hidden
                        >
                          <path d="M6 6l12 12M18 6L6 18" />
                        </svg>
                      </button>
                    ) : null}
                  </div>
                  <button
                    type="button"
                    className={styles.exploreBtn}
                    aria-disabled="true"
                    aria-describedby={exploreNoteId}
                    onClick={(e) => e.preventDefault()}
                  >
                    + Explore drugs
                  </button>
                </div>
                {/* A11Y: the visible reason the explore button is inert,
                    not a tooltip-only title. */}
                <p id={exploreNoteId} className={styles.controlNote}>
                  Explore search is not available yet. It lands with the drug record.
                </p>

                {/* Q2H-0: the four facet chips, the sort and the view
                    toggle. */}
                <div className={styles.filterRow}>
                  <div className={styles.filterGroup}>
                    {FACETS.map((f) => (
                      <FilterDropdown
                        key={f.key}
                        label={f.label}
                        options={options[f.key]}
                        selected={selected[f.key]}
                        onSelect={(v) => toggleFacet(f.key, v)}
                        emptyText={
                          !overview
                            ? "Loading"
                            : facetsUnavailable
                              ? "Filters unavailable"
                              : "Nothing to filter on in this watch"
                        }
                      />
                    ))}
                  </div>
                  <div className={styles.viewGroup}>
                    <FilterDropdown
                      label={`Sort: ${SORT_LABELS[sort]}`}
                      options={SORT_OPTIONS}
                      selected={[sort]}
                      onSelect={(v) => setSort(v as SortKey)}
                      multiple={false}
                      plain
                      alignRight
                    />
                    <button
                      type="button"
                      className={`${styles.viewBtn} ${view === "cards" ? styles.viewBtnOn : ""}`}
                      aria-pressed={view === "cards"}
                      onClick={() => chooseView("cards")}
                    >
                      Cards
                    </button>
                    <button
                      type="button"
                      className={`${styles.viewBtn} ${view === "list" ? styles.viewBtnOn : ""}`}
                      aria-pressed={view === "list"}
                      onClick={() => chooseView("list")}
                    >
                      Compact list
                    </button>
                  </div>
                </div>

                {state.status === "error" ? (
                  <p className={styles.fetchError} role="alert">
                    {errorCopy(state.code)}
                  </p>
                ) : state.status === "loading" ? (
                  <div className={styles.grid} aria-busy="true" aria-label="Loading your drugs">
                    {[0, 1, 2].map((i) => (
                      <div key={i} className={styles.skeleton} aria-hidden="true">
                        <span className={styles.skeletonOrb} />
                        <span className={styles.skeletonBar} style={{ width: "60%" }} />
                        <span className={styles.skeletonBar} style={{ width: "80%" }} />
                        <span className={styles.skeletonBar} style={{ width: "45%" }} />
                      </div>
                    ))}
                  </div>
                ) : drugs.length === 0 ? (
                  /* The watch came back with no drug at all (the Companies
                     page's empty copy, inside the sheet so the hero stands). */
                  <div className={styles.emptyState}>
                    <h2 className={styles.emptyTitle}>Track a drug to see it here</h2>
                    <p className={styles.emptyBody}>
                      Every drug you track lands here as a card, connected to
                      its trials, indications, companies and latest activity.
                    </p>
                    <Link href="/tools/trial-tracker" className={styles.emptyCta}>
                      Track a drug
                    </Link>
                  </div>
                ) : visible.length === 0 ? (
                  /* Two different nothings: this one is about the query and
                     the filters, not the watch. */
                  <p className={styles.fetchNote}>
                    {query.trim()
                      ? `No tracked drug matches “${query.trim()}”. The search covers names, accepted brands, development codes and identifiers.`
                      : "No tracked drug matches every filter you set."}
                  </p>
                ) : view === "cards" ? (
                  <div className={styles.grid} data-view="cards">
                    {visible.map((d) => (
                      <DrugCard
                        key={d.name}
                        card={d}
                        selected={openCard?.name === d.name}
                        onOpen={openQuickView}
                      />
                    ))}
                  </div>
                ) : (
                  <div className={styles.list} data-view="list">
                    {visible.map((d) => (
                      <DrugRow
                        key={d.name}
                        card={d}
                        selected={openCard?.name === d.name}
                        onOpen={openQuickView}
                      />
                    ))}
                  </div>
                )}

                {/* Q5V-0: the scope note. */}
                {scopeLeft ? (
                  <div className={styles.scopeNote}>
                    <p>
                      {scopeLeft}
                      {noFacetNote ? ` · ${noFacetNote}` : ""}
                    </p>
                    <p>Search also recognises accepted brands and development codes</p>
                  </div>
                ) : null}
              </>
            }
          />
        </DrugsCanvas>

        {openCard && overview ? (
          <DrugQuickView
            key={openCard.name}
            card={openCard}
            watchName={overview.landscape.name}
            opener={openerRef.current}
            onClose={closeQuickView}
          />
        ) : null}
      </div>
    </DrugIdentityProvider>
  )
}
